"use client";

import { useState } from "react";
import Chip from "../chip/Chip";
import DropDown from "../utils/dropdown-item/DropDown";
import EmptyBox from "../emptystate/EmptyBox";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { cn } from "../../lib/utils";
import LoadingBox from "../loading/LoadingBox";

const MultiSelect = ({
	listItems,
	groupsListItems,
	singleSelection,
	selectedItems,
	onSelect = () => {},
	classNameOverlay = "",
	placeholder,
	searchProps,
	isLoading,
	disabled,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	// Normalize to a groups structure so there is a single rendering path:
	// a flat `listItems` becomes a single untitled group.
	const hasGroups = Array.isArray(groupsListItems) && groupsListItems.length > 0;
	const groups = hasGroups ? groupsListItems : [{ label: null, listItems: listItems ?? [] }];
	const hasAnyItem = groups.some((group) => group.listItems?.length > 0);

	/**
	 * Listener Checkbox
	 * @param {boolean} isChecked
	 * @param {option selected} option
	 */
	function onChangeCheck(isChecked, data) {
		if (singleSelection) {
			onSelect(isChecked ? data : null);
		} else if (isChecked) {
			onSelect([...selectedItems, data]);
		} else {
			onSelect(selectedItems.filter((item) => String(item.id) !== String(data.id)));
		}
	}

	const removeItem = (event, item) => {
		event.preventDefault();
		event.stopPropagation();
		onChangeCheck(false, item);
	};

	const renderItem = (data, key) => (
		<div key={key}>
			<div className="check_landing small flex">
				<input
					type="checkbox"
					checked={!!selectedItems.find((item) => item.id === data.id)}
					onChange={(e) => onChangeCheck(e.target.checked, data)}
					id={`${data.id}`}
				/>
				<label htmlFor={`${data.id}`} className="icon">
					{data.icon && <span className={data.icon}></span>}
					{data.label}
				</label>
			</div>
		</div>
	);

	const renderToggle = () => (
		<div
			className={cn(
				"flex p-[11px_12px] justify-between rounded-[8px] font-semibold text-[12px] gap-[8px] select-none",
				disabled
					? "bg-dark-70/10 text-dark-40 cursor-default"
					: "bg-interactive-5 text-interactive-100 cursor-pointer"
			)}
		>
			<div className="flex gap-[2px] items-center w-0 flex-1">
				<div className="truncate">{placeholder ?? "Seleziona elementi"}</div>
			</div>
			<span
				data-icon-arrow="1"
				className={cn(
					"icon_16 default plus icon_mask !bg-interactive-100 flex-none",
					disabled && "grayscale"
				)}
			/>
		</div>
	);

	// Disabled: nessuna interazione, si mostra solo il toggle
	if (disabled) return <div>{renderToggle()}</div>;

	return (
		<div>
			{selectedItems.length > 0 && (
				<div className="flex flex-wrap gap-3 pb-4">
					{selectedItems.map((item, i) => (
						<Chip
							key={i}
							onClick={(event) => removeItem(event, item)}
							icon={item.icon}
							label={item.label}
							rightContent={
								<span
									className={cn(
										"icon_10 default close flex-none ml-1 icon_mask !bg-dark-30",
										item?.removeProps?.className
									)}
								/>
							}
							{...item?.chipProps}
						/>
					))}
				</div>
			)}
			<DropDown onToggle={setIsOpen}>
				{/* Dropdown Toggle */}
				<DropDown.Toggle>{renderToggle()}</DropDown.Toggle>

				{/* DropDown Content */}
				<DropDown.Content className="w-full">
					{({ closeDropDown }) => (
						<div className="bg-white z-30 overflow-auto shadow-[0_8px_20px] shadow-dark-100/10 ring-1 ring-dark-10 rounded-[10px]">
							{searchProps && (
								<div className="px-5 pt-5">
									<div className="pb-5 shadow-[inset_0_-1px] shadow-dark-10">
										<div className="wrap_input_landing icon">
											<span className="icon_16 default search" />
											<input
												type="text"
												className="landing"
												placeholder="Cerca..."
												value={searchProps.value}
												onChange={(e) => searchProps.handler(e.target.value)}
											/>
										</div>
									</div>
								</div>
							)}
							{isLoading ? (
								<LoadingBox blocked="true" />
							) : hasAnyItem ? (
								<OverlayScrollbarsComponent
									defer
									options={{ scrollbars: { autoHide: "never" } }}
									className={cn("overflow-auto p-5 max-h-[250px]", classNameOverlay)}
								>
									<div className="grid gap-6">
										{groups.map((group, gi) => (
											<div key={gi} className="grid gap-4">
												{group.label && (
													<div className="text-12 font-bold uppercase text-dark-50">
														{group.label}
													</div>
												)}
												{group.listItems?.map((data, i) => renderItem(data, `${gi}-${i}`))}
											</div>
										))}
									</div>
								</OverlayScrollbarsComponent>
							) : (
								<div className="flex flex-col justify-center items-center text-center">
									<EmptyBox
										small={true}
										icon="icon_75 default archive_plan"
										title="Nessun risultato trovato"
										description="Non ci sono risultati da selezionare"
									/>
								</div>
							)}
						</div>
					)}
				</DropDown.Content>
			</DropDown>
		</div>
	);
};

export default MultiSelect;
